import type { PlanTemplateStep } from '@/@studio-ai/types';
import { formatAnswer } from '../wizard/format-answer';

/**
 * Handoff between the Reports build form and the DANI planner.
 *
 * The Reports page lives outside `StudioAIProvider` (which mounts under
 * /dani), so it cannot call `sendSilentMessage` itself — navigating away
 * would abort the SSE stream. Instead the form stores a launch payload in
 * sessionStorage and navigates to `/dani?launch=reports`; the DANI landing
 * page consumes the payload inside the provider and kicks off the template
 * via `handleTemplateSelect`.
 */

export const REPORTS_LAUNCH_QUERY_PARAM = 'launch';
export const REPORTS_LAUNCH_QUERY_VALUE = 'reports';
export const REPORTS_LAUNCH_URL = `/dani?${REPORTS_LAUNCH_QUERY_PARAM}=${REPORTS_LAUNCH_QUERY_VALUE}`;

const LAUNCH_STORAGE_KEY = 'mfour.reports.launch';
const LAUNCH_TTL_MS = 5 * 60 * 1000;

export interface ReportsLaunchPayload {
    templateId: number;
    templateTitle: string;
    prompt: string;
    /**
     * What the finished report should be called. Composed from the builder's
     * own answers, because the server-side default is unreliable — real runs
     * have produced both "Audience Profiling - Panera Bread" and
     * "New Report - 2026-04-06T15:32:27.513Z".
     *
     * Optional: absent just means the server's title stands.
     */
    reportTitle?: string;
    /**
     * The client the report was set up for under staff View-as (M4S-5670 §7):
     * its SF account id, or null when it was set up as the staff member. The
     * DANI landing refuses a payload whose stamp is not the tab's current
     * View-as client, so a report is never launched as the wrong client.
     * Absent on payloads stored before the field existed, and read as null.
     */
    viewAsSfAccountId?: string | null;
    ts: number;
}

export function storeReportsLaunch(payload: Omit<ReportsLaunchPayload, 'ts'>): void {
    try {
        sessionStorage.setItem(
            LAUNCH_STORAGE_KEY,
            JSON.stringify({ ...payload, ts: Date.now() } satisfies ReportsLaunchPayload),
        );
    } catch {
        // Storage unavailable (private mode/quota) — the landing page will
        // simply show the normal DANI landing instead of auto-launching.
    }
}

/**
 * Drops any pending launch without reading it. Starting, switching or clearing
 * View as calls this, so a report set up for one client is never launched as
 * another (M4S-5670 §4).
 */
export function discardReportsLaunch(): void {
    try {
        sessionStorage.removeItem(LAUNCH_STORAGE_KEY);
    } catch {
        // Storage unavailable: there is no payload to discard.
    }
}

/** Reads and deletes the launch payload; null when absent, invalid, or stale. */
export function consumeReportsLaunch(): ReportsLaunchPayload | null {
    try {
        const raw = sessionStorage.getItem(LAUNCH_STORAGE_KEY);
        if (!raw) return null;
        sessionStorage.removeItem(LAUNCH_STORAGE_KEY);
        const parsed = JSON.parse(raw) as Partial<ReportsLaunchPayload>;
        if (
            typeof parsed.templateId !== 'number' ||
            typeof parsed.templateTitle !== 'string' ||
            typeof parsed.prompt !== 'string' ||
            typeof parsed.ts !== 'number' ||
            Date.now() - parsed.ts > LAUNCH_TTL_MS
        ) {
            return null;
        }
        return parsed as ReportsLaunchPayload;
    } catch {
        return null;
    }
}

/**
 * Whether a launch payload was set up for the client the tab is viewing as now.
 * Both sides read null as "the staff member, not viewing as anyone".
 */
export function launchMatchesViewAs(
    payload: Pick<ReportsLaunchPayload, 'viewAsSfAccountId'>,
    currentSfAccountId: string | null | undefined,
): boolean {
    return (payload.viewAsSfAccountId ?? null) === (currentSfAccountId ?? null);
}

/**
 * Composes the silent kickoff prompt sent as the first planner message.
 * The template's agentInstructions recognize the INPUTS block and skip the
 * step wizard, going straight to the plan summary card.
 */
export function buildKickoffPrompt(
    templateName: string,
    steps: PlanTemplateStep[],
    answers: Record<string, string>,
): string {
    const lines = steps.map((step) => {
        // `select`/`multi_select` answers are stored as option *ids* — row 9 of
        // studioai_plan_templates stores literal `option_1 … option_6` — and a
        // template's instructions describe its options by label and never define
        // those tokens. Sending ids is the same defect as sending a bare question
        // id: the planner cannot resolve the input and reports it does not exist.
        //
        // `formatAnswer` is the mapper because it already carries the two guards
        // this needs: it splits the comma-joined value before looking anything up,
        // and it returns free text untouched when the step has no options — which
        // is what keeps `importantquestion`'s "P2 — question, with commas" intact.
        const formatted = formatAnswer(step, answers[step.id] ?? '');
        // Multi-line answers (term lists, brand sets) collapse to a single
        // pipe-joined line — the INPUTS block is strictly one line per step,
        // and the planner's INPUT PARSING CONVENTIONS split on `|`. Option
        // labels are already comma-joined by `formatAnswer` and pass through
        // this untouched.
        const value = formatted
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean)
            .join(' | ');
        return `- ${step.id}: ${value.length > 0 ? value : '(skipped)'}`;
    });
    return [
        `Build the "${templateName}" report with these inputs. All intake steps are answered — do not re-ask them.`,
        'INPUTS',
        ...lines,
    ].join('\n');
}

/** Honest build-time copy per depth (the planner's own kickoff message says 15–30 min). */
export function estimateBuildTime(depth: string | undefined): string {
    switch (depth) {
        case 'universe_check':
            return 'a few minutes';
        case 'topline':
            return '10–20 minutes';
        default:
            return '15–30 minutes';
    }
}
